import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AxiosError} from "axios";
import {isEqual} from "lodash";
import {useTypedSelector} from "../../redux/Hooks/storeSelectors";
import {
    selectCurrentProjectId,
    selectEntity,
} from "../../redux/selectors/selectors";
import {isErrorResponse, isRestApiErrorResponse} from "../../utils/utils";
import {
    MetadataPropertyEntity,
    MetadataPropertyOptionEntity,
    MetadataPropertyType
} from "../../api/entities/replancity_MetadataProperty";
import {projectsApi} from "../../api/projectsApi";
import {BaseEntity, BaseEntityNameLess} from "../../api/entities/BaseEntity";
import {setEntity} from "../../redux/entity/entity-reducer";
import {entityApi, EntityView, SaveEntityProps} from "../../api/entityApi";
import {useDispatch} from "react-redux";
import {useToastContext} from "../../context/toastContext";
import {useTranslation} from "react-i18next";
import {serviceApi} from "../../api/serviceApi";
import {DataById} from "../../redux/map/types";
import {usePresetsContext} from "../../context/presetsContext";
import {EntityFormMetadataProperty} from "../../components/Entities/EntityForm/EntityForm";
import {Option} from "../../components/form/SelectBox/SelectBox";
import {FormDataObj, PartiallyOptional} from "../../utils/types";
import {SelectedFeatureProperties} from "../../redux/showInfo/types";
import {PublicTransitLayerEntityName} from "../../api/entities/replancity_PublicTransitLine";
import {getSessionStorageItem, setSessionStorageItem} from "../../utils/storageUtils";


export type UseEntityLoaderProps = {
    entityName: string;
    saveFnArgs?: (entity: any) => ({ [key: string]: any });
    presetProperty?: string;
    groupUpdateTaskArgument?: string;
    entityView?: EntityView;
}

export type EntityFormMetadata = Omit<EntityFormMetadataProperty, 'options'> & { options: Option[] };

export type VisibleMetadataByName = {
    [key: string]: {
        subscribers: string[],
        defaultValue: any;
        // eslint-disable-next-line @typescript-eslint/ban-types
        showIf: Function;
        // eslint-disable-next-line @typescript-eslint/ban-types
        mandatoryIf: Function;
        options?: {
            value: string;
            caption: string;
        }[]
    } & EntityFormMetadata
}

export type EntityMetadata = {
    visibleMetadataByName: VisibleMetadataByName;
    orderedNames: string[];
}

const fn = () => false;

const createShowIfFunction = function (fieldName: string, likeShowIfProp?: string) {
    if (likeShowIfProp) {
        return new Function(`
            try {
                return ${likeShowIfProp};
            } catch (e) {
                console.error(\`Corrupted ${likeShowIfProp}: "${likeShowIfProp}" for "${fieldName}" metadata\\n\`, e);
            return () => false;
        }`);
    }
    return fn
}

const metadataOptionsToSelectOptions = (type: MetadataPropertyType, options: MetadataPropertyOptionEntity[]) => options.map(({
                                                                                                                                 id,
                                                                                                                                 propertyName,
                                                                                                                                 printedName
                                                                                                                             }) => ({
    value: type === 'Enum' ? propertyName : id,
    caption: printedName ?? propertyName
}));

export const getVisibleEntityMetadata = (metaData: MetadataPropertyEntity[], entityDraft: DataById<unknown> = {}): {
    metadata: EntityMetadata,
    entityDraft: DataById<unknown>,
} => {
    //TODO move to separate class/method
    const orderedNames: string[] = [];
    const visibleMetadataByName: VisibleMetadataByName =
        metaData
            .filter(({showIf}) => !!showIf && showIf !== 'false')
            .reduce((acc, meta) => {
                orderedNames.push(meta.name);

                acc[meta.name] = {
                    ...meta,
                    options: metadataOptionsToSelectOptions(meta.type, meta.options),
                    showIf: createShowIfFunction(meta.name, meta.showIf),
                    mandatoryIf: createShowIfFunction(meta.name, meta.mandatoryIf)
                }

                const {
                    name,
                    type,
                    showIf,
                    mandatoryIf,
                    defaultValue,
                    options
                } = meta;

                const deps = showIf.match(/(?<=\bthis\.)\w+/g);
                if (deps) {
                    deps.forEach(dep => {
                        if (!acc[dep]) {
                            // @ts-ignore
                            acc[dep] = {
                                subscribers: []
                            };
                        }
                        if (!acc[dep].subscribers) {
                            acc[dep].subscribers = [];
                        }

                        acc[dep].subscribers.push(name);
                    })
                }

                acc[name].showIf = createShowIfFunction(name, showIf);
                acc[name].mandatoryIf = createShowIfFunction(name, mandatoryIf)
                acc[name].options = metadataOptionsToSelectOptions(type, options);

                let curValue = '';
                if (options.length) {
                    curValue = type === 'Enum' ? options[0].propertyName : options[0].id;
                }
                if (defaultValue) {
                    curValue = defaultValue
                }
                acc[name].defaultValue = curValue;

                // metadata value should be used if showIf and
                // entityDraft isn't saved in sessionStorage
                // or there isn't such key in stored data
                if (acc[name].showIf.apply(entityDraft) && !entityDraft[name]) {
                    entityDraft[name] = curValue;
                }

                //TODO end

                return acc;
            }, {});

    return {metadata: {visibleMetadataByName, orderedNames}, entityDraft};
};

export const getVisibleFieldsByFormChange = ({fieldName, visibleMetadataByName, formDataObj}: {
    visibleMetadataByName: VisibleMetadataByName;
    fieldName: string;
    formDataObj: DataById<unknown>;
}) => {

    const getNewFormDataObj = ({fieldName, visibleMetadataByName, formDataObj}: {
        visibleMetadataByName: VisibleMetadataByName;
        fieldName: string;
        formDataObj: DataById<unknown>;
    }) => {
        if (!visibleMetadataByName?.[fieldName]?.subscribers) {
            return formDataObj;
        }

        visibleMetadataByName?.[fieldName].subscribers.forEach(subscriber => {
            if (visibleMetadataByName[subscriber].showIf.apply(formDataObj)) {
                formDataObj[subscriber] = Object.hasOwn(formDataObj, subscriber) ? formDataObj[subscriber] : visibleMetadataByName[subscriber].defaultValue ?? '';
            } else {
                delete formDataObj[subscriber];
            }
            return getNewFormDataObj({fieldName: subscriber, visibleMetadataByName, formDataObj});
        })

        return formDataObj;
    }

    return getNewFormDataObj({fieldName, visibleMetadataByName, formDataObj: {...formDataObj}});
}

export const useEntityLoader = <T extends BaseEntityNameLess>({
                                                                  entityName,
                                                                  saveFnArgs,
                                                                  presetProperty,
                                                                  groupUpdateTaskArgument
                                                              }: UseEntityLoaderProps = {} as UseEntityLoaderProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<AxiosError | null>(null);
    //TODO ideally local state should be used, but Redux is used by breadcrumbs nowadays
    // const [entity, setEntity] = useState<any>({});
    const entity = useTypedSelector((state) => selectEntity(state, entityName));
    const [initialEntity, setInitialEntity] = useState<PartiallyOptional<typeof entity, 'id'> | null>(null);
    const [metadata, setMetadata] = useState<EntityMetadata>();
    // const metadata = useTypedSelector<MetadataPropertyEntity[]>((state) => selectEntityMetadata(state, entityName));
    const projectId = useTypedSelector(selectCurrentProjectId);
    const {selectedPresetId} = usePresetsContext();
    const abortControllerRef = useRef(new AbortController());
    const dispatch = useDispatch();
    const {addToast} = useToastContext();
    const {t} = useTranslation();

    useEffect(() => {
        return () => {
            setError(null);
        }
    }, []);

    const unsavedEntityChanges = useMemo(() => !isEqual(initialEntity, entity), [initialEntity, entity])

    const loadMetadata = useCallback(async (entityName: string, selectedPresetId: string): Promise<MetadataPropertyEntity[]> => {
        return await projectsApi.fetchEntityMetadata({
            entityName,
            presetId: selectedPresetId,
            abortSignal: abortControllerRef.current.signal
        });
    }, [])

    const loadEntity = useCallback(async ({entityName, entityId, entityView}: {
        entityName: string;
        entityId: string;
        entityView?: EntityView
    }): Promise<T> => {
        // try {
        const resp = await entityApi.getEntity<T>({
            entityId: entityId,
            entityName,
            ...(
                entityView ?
                    {
                        params: {
                            view: entityView
                        }
                    }
                    : {}
            ),
            abortSignal: abortControllerRef.current.signal
        });

        //TODO call flyTo util? example was useRoadSegmentLoader

        return resp;
    }, [])

    const saveFn = useCallback(async ({entityName, id, data: entity}: SaveEntityProps): Promise<T> => {
        setLoading(true);

        //removed from sessionStorage on user logout
        const sessStorDataStr = getSessionStorageItem("Replan.EntityDataByEntityName");
        const sessStorData = sessStorDataStr ? JSON.parse(sessStorDataStr) : {};
        setSessionStorageItem("Replan.EntityDataByEntityName", JSON.stringify({
            ...(sessStorData ? sessStorData : {}),
            [entityName]: entity
        }));

        const resp = await entityApi.saveEntity<T>({
            id,
            entityName,
            data: {
                ...(presetProperty ?
                    {
                        [presetProperty]: {
                            id: selectedPresetId
                        }
                    } : {}),
                ...entity,
                ...saveFnArgs?.(entity),
            },
            abortSignal: abortControllerRef.current.signal
        });

        if (isErrorResponse(resp)) {
            setError(entity as any);
        }

        setLoading(false);

        return resp;
    }, [projectId, selectedPresetId, saveFnArgs])


    //TODO maybe move somewhere
    const groupSaveFn = useCallback(async ({
                                               selectedFeatures,
                                               data,
                                           }: { selectedFeatures: SelectedFeatureProperties[]; data: any }) => {
        let resp;
        if (groupUpdateTaskArgument === 'roadInfrastructureUpdateTask') {
            resp = await serviceApi.groupUpdate(
                {
                    [groupUpdateTaskArgument]: {
                        source: {...data},
                        targets: selectedFeatures.map(({id}) => id)
                    } as any
                },
                abortControllerRef.current.signal
            );
        }
        if (groupUpdateTaskArgument === 'transitRouteUpdateTask') {
            //TODO pass PublicTransitLayerEntityName.ROUTES as sourceEntityName?
            const ids = selectedFeatures
                .filter(({entityName}) => entityName === PublicTransitLayerEntityName.ROUTES)
                .map(({id}) => id);
            const {vehicleType, ...rest} = data;
            resp = await serviceApi.ptRoutesGroupUpdate(
                {
                    [groupUpdateTaskArgument]: {
                        source: {
                            ...(vehicleType ? {vehicleType: {id: vehicleType}} : {}),
                            ...rest
                        },
                        targets: ids
                    } as any
                },
                abortControllerRef.current.signal
            );
        }

        return resp;
    }, [])

    const groupDeleteFn = useCallback(async ({
                                                 presetId,
                                                 ids,
                                             }) => {
        return await serviceApi.groupManipulate(
            {
                task: {
                    presetId,
                    type: 'DELETE',
                    targets: ids
                }
            },
            abortControllerRef.current.signal
        );
    }, [])

    const deleteFn = useCallback(async ({id}: { id: string; }) => {
        setLoading(true);

        const resp = await entityApi.deleteEntity({
            entityId: id,
            entityName,
            abortSignal: abortControllerRef.current.signal
        });
        if (!isErrorResponse(resp)) {
            addToast(`${t('common.delete-successful')}`, 'success');
        }
        setLoading(false);

        return resp;
    }, [entityName])

    const loadData = useCallback(async ({entityName, entityId, entityView}: {
        entityName: string;
        entityId?: string | null | undefined;
        entityView?: EntityView
    }): Promise<void> => {
        setLoading(true);

        const [_metaData, entity] = await Promise.all([
            loadMetadata(entityName, selectedPresetId),
            ...(entityId ? [loadEntity({
                entityName,
                entityId,
                entityView
            })] : [])])

        if (isErrorResponse(_metaData) || isRestApiErrorResponse(_metaData)) {
            console.error('Replan. useEntityLoader.loadMetadata error:', JSON.stringify(metadata));
            setError(_metaData as any);

            setLoading(false);
            return;
        }

        if (isErrorResponse(entity) || isRestApiErrorResponse(entity)) {
            console.error('Replan. useEntityLoader.loadEntity error:', entity);
            setError(entity as any);

            setLoading(false);
            return;
        }

        if (_metaData) {
            const entityDataByEntityNameStr = getSessionStorageItem("Replan.EntityDataByEntityName");
            const entityDataByEntityName = entityDataByEntityNameStr ? JSON.parse(entityDataByEntityNameStr) : {};
            const {
                metadata,
                entityDraft
            } = getVisibleEntityMetadata(_metaData, entityDataByEntityName?.[entityName] ?? {});

            setMetadata(metadata);
            // dispatch(setEntityMetadata({entityName, metadata: visibleMetadata}));

            // setEntity({
            //     ...entityDraft,
            //     ...(entityId ? entity : {})
            // });
            setInitialEntity({
                ...entityDraft,
                ...(entityId ? entity : {})
            } as any);
            dispatch(setEntity({
                entityName,
                entity: {
                    ...entityDraft,
                    ...(entityId ? entity : {})
                }
            }));
        }

        setLoading(false);
    }, [loadMetadata, loadEntity, selectedPresetId]);

    const changeForm = useCallback((formDataObj: FormDataObj) => {
        dispatch(setEntity({entityName, entity: {...formDataObj}}));
    }, [entity])

    return {
        loading,
        entity: entity as T ?? {},
        unsavedEntityChanges,
        metadata,
        loadData,
        loadMetadata,
        changeForm,
        saveFn,
        groupSaveFn,
        groupDeleteFn,
        deleteFn,
        error
    };
};
